import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { db, FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { MainHeadline, SubDescription } from 'components/Typography'
import { useSelect, useString, useWysiwygEditor } from 'hooks/form'
import { useNumberChangeEvent } from 'hooks/form'
import { Spacer } from 'components/Utility'
import {
  ProductDefaultBaseSection,
  ProductDefaultDailySchedule,
  ProductDefaultPreorderSchedule,
  ProductEditAllergensSection,
  ProductEditDelivery,
} from 'components/Product'
import { getCategories } from 'reducks/category/selectors'
import { fetchEnabledCategories } from 'reducks/category/operations'
import { FormLabel } from 'components/Label'
import { TextInput } from 'components/Form'
import { OrangeButton } from 'components/Button'
import { StockUnionType } from 'types/stock'
import { openCautionModal } from 'reducks/modal/operations'
import { DefaultEditError } from 'components/Error'
import { useHandleAllergens } from 'hooks/bread'
import {
  StoreDefaultBreadType,
  StoreDefaultDeliveryBreadType,
  StoreDefaultScheduleType,
  StoreDefaultType,
} from 'types/storeDefault'
import {
  useAddDefaultSchedule,
  useAddDefaultStock,
  useDeleteDefaultSchedule,
  useDeleteDefaultStock,
  useFetchStoreDefault,
  useFilledDefaultRequiredForm,
  useHandleDefaultHoursToDeadline,
  useHandleDefaultScheduledGeneration,
  useHandleDefaultStock,
  useInitDefaultSchedulesByType,
  useSelectDefaultAllDaysOfWeek,
  useSelectDefaultDaysOfWeek,
  useSelectDefaultMaximumOrder,
  useStoreDeliveryDefault,
  useToggleDefaultScheduleType,
  useToggleDefaultTypeDaily,
  useUnselectDefaultAllDaysOfWeek,
  useUpdateDefaultNote,
} from 'hooks/storeDefault'
import { saveDefault } from 'reducks/storeDefault/operations'
import { OptionType } from 'types/schedule'
import { PackageSize } from 'types/storeDeliveryFee'

export const getUpdateSchedulesFunction = (
  type: StockUnionType | '',
  setDailySchedules: Dispatch<SetStateAction<StoreDefaultScheduleType[]>>,
  setPreorderSchedules: Dispatch<SetStateAction<StoreDefaultScheduleType[]>>
): Dispatch<SetStateAction<StoreDefaultScheduleType[]>> | undefined => {
  switch (type) {
    case 'daily':
      return setDailySchedules
    case 'preorder':
      return setPreorderSchedules
    default:
      return undefined
  }
}

const ProductDefaultEditTemplate: FC = () => {
  const dispatch = useDispatch()
  const categories = useSelector((state: State) => getCategories(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const { allergens, isSetAllergens, handleAllergens, handleSetAllergens, setAllergens, setIsSetAllergens } =
    useHandleAllergens()
  const {
    currentLength: currentDescriptionLength,
    editorState: description,
    initEditorState: setDescription,
    onEditorStateChange: handleDescription,
    parseEditorStateToHtml: parseDescriptionToHtml,
    replaceImage,
  } = useWysiwygEditor()
  const { state: categoryId, handleState: selectCategoryName, setState: setCategoryId } = useSelect<string>('')
  const {
    state: expirationDate,
    handleState: handleExpirationDate,
    setState: setExpirationDate,
  } = useString<string>('')
  const [price, setPrice] = useState<number>(0)
  const [isScheduledGeneration, setScheduledGeneration] = useState<boolean>(false)
  const [isTypeDaily, setIsTypeDaily] = useState<boolean>(false)
  const [isTypePreorder, setIsTypePreorder] = useState<boolean>(false)
  const [isTypeDelivery, setIsTypeDelivery] = useState<boolean>(false)
  const [isTypeShared, setIsTypeShared] = useState<boolean>(false)
  const [dailySchedules, setDailySchedules] = useState<StoreDefaultScheduleType[]>([])
  const [preorderSchedules, setPreorderSchedules] = useState<StoreDefaultScheduleType[]>([])
  const [storeDefaultBreadRef, setStoreDefaultBreadRef] = useState<FirebaseDocRefType>(
    db.collection('stores').doc(storeRef.id).collection('store_defaults').doc().collection('store_default_breads').doc()
  )
  const { storeDefault, storeDefaultBread, storeDefaultDeliveryBread, storeDefaultSchedules } = useFetchStoreDefault()

  const handlePrice = useNumberChangeEvent(setPrice)
  const handleScheduledGeneration = useHandleDefaultScheduledGeneration(setScheduledGeneration, setDailySchedules)
  const addSchedule = useAddDefaultSchedule(storeDefaultBreadRef, setDailySchedules, setPreorderSchedules)
  const deleteSchedule = useDeleteDefaultSchedule(setDailySchedules, setPreorderSchedules)
  const addDefaultStock = useAddDefaultStock(setDailySchedules, setPreorderSchedules)
  const deleteDefaultStock = useDeleteDefaultStock(setDailySchedules, setPreorderSchedules)
  const handleDefaultStocks = useHandleDefaultStock(setDailySchedules, setPreorderSchedules)
  const handlePreorderHoursToDeadline = useHandleDefaultHoursToDeadline(setPreorderSchedules)
  const selectMaximumOrder = useSelectDefaultMaximumOrder(setDailySchedules, setPreorderSchedules)
  const updateNote = useUpdateDefaultNote(setDailySchedules, setPreorderSchedules)
  const selectDaysOfWeek = useSelectDefaultDaysOfWeek(setDailySchedules, setPreorderSchedules)
  const selectAllDaysOfWeek = useSelectDefaultAllDaysOfWeek(setDailySchedules, setPreorderSchedules)
  const unselectAllDaysOfWeek = useUnselectDefaultAllDaysOfWeek(setDailySchedules, setPreorderSchedules)

  // 注文タイプのチェックを外す際に受付設定を初期化する
  const initSchedulesByType = useInitDefaultSchedulesByType(
    getUpdateSchedulesFunction,
    setDailySchedules,
    setPreorderSchedules,
    setIsTypeDaily,
    setIsTypePreorder,
    setIsTypeShared,
    setIsTypeDelivery
  )

  const openDeleteScheduleCautionModal = useCallback(
    (modalType: StockUnionType) => {
      dispatch(
        openCautionModal(
          '変更する',
          'キャンセル',
          'すでに登録されている情報は削除されてしまいます。一時的な変更であれば、在庫を０にするや、受渡可能日を設定しないなどお試しください。',
          () => initSchedulesByType(modalType),
          '注文タイプを変更します'
        )
      )
    },
    [dispatch, initSchedulesByType]
  )

  // 注文タイプのチェックを外す時にモーダルを表示する
  const toggleIsTypeDaily = useToggleDefaultTypeDaily(
    storeDefaultBreadRef,
    setIsTypeDaily,
    openDeleteScheduleCautionModal,
    setDailySchedules,
    setScheduledGeneration
  )
  const toggleIsTypePreorder = useToggleDefaultScheduleType(
    storeDefaultBreadRef,
    openDeleteScheduleCautionModal,
    setPreorderSchedules,
    setIsTypePreorder,
    'preorder'
  )
  const storeDeliveryBreadForm = useStoreDeliveryDefault(
    storeDefaultBreadRef,
    isTypeDelivery,
    storeRef,
    storeDefaultDeliveryBread,
    setIsTypeDelivery
  )

  // 当日取り置きの受渡曜日・受渡解禁時刻・オープン時在庫数に応じて、在庫が自動登録か都度配信かを判定する
  // 初回レンダリング時の判定はスキップする
  useEffect(() => {
    if (dailySchedules.length === 0) return
    const isScheduled = dailySchedules.some((dailySchedule) => {
      return (
        dailySchedule.days_of_week.length > 0 ||
        dailySchedule.default_stocks.some((defaultStock) => {
          return defaultStock.pickupable_time !== '' || defaultStock.stock > 0
        })
      )
    })
    setScheduledGeneration(isScheduled)
  }, [dailySchedules])

  useEffect(() => {
    if (!storeDefaultBread) return
    setStoreDefaultBreadRef(storeDefaultBread.store_default_bread_ref)
    setAllergens([...storeDefaultBread.allergens])
    setDescription(storeDefaultBread.description)
    setPrice(storeDefaultBread.price)
    setIsSetAllergens(storeDefaultBread.is_set_allergens)
    setIsTypeDaily(storeDefaultBread.is_type_daily)
    setIsTypePreorder(storeDefaultBread.is_type_preorder)
    setIsTypeShared(storeDefaultBread.is_type_shared || false)
    setIsTypeDelivery(storeDefaultBread.is_type_delivery || false)
  }, [storeDefaultBread])

  // カテゴリの初期値を設定
  useEffect(() => {
    if (!categories || categories.length === 0) return
    if (!storeDefaultBread || !storeDefaultBread.category) {
      setCategoryId(categories[0].category_ref.id)
    } else {
      setCategoryId(storeDefaultBread.category.id)
    }
  }, [storeDefaultBread, categories])

  // 受付設定に応じて値をセット
  useEffect(() => {
    // 受付設定をtypeで分ける
    setDailySchedules([...storeDefaultSchedules.filter((schedule) => schedule.type === 'daily')])
    setPreorderSchedules([...storeDefaultSchedules.filter((schedule) => schedule.type === 'preorder')])
    const schedule = storeDefaultSchedules[0]
    if (!schedule && !storeDefaultDeliveryBread) return
    // 賞味期限を取得する
    const expirationDateOption = schedule
      ? schedule.options.find((option) => option.key === '賞味期限')
      : storeDefaultDeliveryBread?.options.find((option) => option.key === '賞味期限')
    setExpirationDate(expirationDateOption?.value || '')
  }, [storeDefaultSchedules, storeDefaultDeliveryBread])

  // カテゴリー一覧を取得する
  useEffect(() => {
    if (categories.length > 0) return
    dispatch(fetchEnabledCategories())
  }, [])

  // 必須項目を全て入力し、当日取り置きか事前予約の受付設定が完了したら確認画面へのボタンを活性化
  const isFilled = useFilledDefaultRequiredForm({
    description: parseDescriptionToHtml(),
    categoryId,
    isScheduledGeneration,
    isTypeDaily,
    dailySchedules,
    isTypePreorder,
    preorderSchedules,
    isTypeDelivery,
    storeDeliveryBreadForm,
  })

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'商品情報のテンプレート設定'} />
      <Spacer size={20} />
      <ProductDefaultBaseSection
        storeDefaultId={storeDefaultBreadRef.id}
        storeId={storeRef.id}
        categories={categories}
        categoryId={categoryId}
        currentDescriptionLength={currentDescriptionLength}
        description={description}
        handleDescription={handleDescription}
        handlePrice={handlePrice}
        price={price}
        replaceImage={replaceImage}
        selectCategoryName={selectCategoryName}
      />
      <Spacer size={28} />
      <ProductEditAllergensSection
        allergens={allergens}
        handleAllergens={handleAllergens}
        handleSetAllergens={handleSetAllergens}
        isSetAllergens={isSetAllergens}
      />
      <Spacer size={28} />
      <FormLabel id={'order-type'} label={'注文タイプ'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={
          '当日取り置きできる商品と事前に予約できる商品として、注文できます。取り扱い方法にチェックを入れてください。'
        }
      />
      <Spacer size={8} />
      <ProductDefaultDailySchedule
        addDefaultStock={addDefaultStock}
        addSchedule={addSchedule}
        deleteDefaultStock={deleteDefaultStock}
        deleteSchedule={deleteSchedule}
        handleDefaultStocks={handleDefaultStocks}
        handleScheduledGeneration={handleScheduledGeneration}
        isChecked={isTypeDaily}
        isScheduledGeneration={isScheduledGeneration}
        schedules={dailySchedules}
        selectDaysOfWeek={selectDaysOfWeek}
        selectMaximumOrder={selectMaximumOrder}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        setDailySchedules={setDailySchedules}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
        toggleCheckbox={toggleIsTypeDaily}
        type={'daily'}
        updateNote={updateNote}
      />
      <Spacer size={8} />
      <ProductDefaultPreorderSchedule
        addDefaultStock={addDefaultStock}
        addSchedule={addSchedule}
        deleteDefaultStock={deleteDefaultStock}
        deleteSchedule={deleteSchedule}
        handleDefaultStocks={handleDefaultStocks}
        handleHoursToDeadline={handlePreorderHoursToDeadline}
        label={'事前予約'}
        isChecked={isTypePreorder}
        schedules={preorderSchedules}
        selectDaysOfWeek={selectDaysOfWeek}
        selectMaximumOrder={selectMaximumOrder}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        setPreorderSchedules={setPreorderSchedules}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
        toggleCheckbox={toggleIsTypePreorder}
        type={'preorder'}
        updateNote={updateNote}
      />
      <Spacer size={8} />
      <ProductEditDelivery isChecked={isTypeDelivery} label={'取り寄せ'} {...storeDeliveryBreadForm} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        label={'賞味期限'}
        onChange={handleExpirationDate}
        required={false}
        size={'small'}
        type={'text'}
        value={expirationDate}
      />
      <Spacer size={48} />
      <OrangeButton
        disabled={!isFilled}
        label={'保存する'}
        onClick={() => {
          const now = FirebaseTimestamp.now()
          const category = categories.find((category) => category.category_ref.id === categoryId)
          const nextStoreDefault: StoreDefaultType = {
            is_primary: storeDefault?.is_primary ?? true,
            created_at: storeDefault?.created_at ?? now,
            updated_at: storeDefault?.updated_at ?? now,
          }
          const nextStoreDefaultBread: StoreDefaultBreadType = {
            allergens: allergens,
            description: parseDescriptionToHtml(),
            category: category.category_ref,
            created_at: storeDefaultBread?.created_at || now,
            is_set_allergens: isSetAllergens,
            is_type_daily: isTypeDaily,
            is_type_preorder: isTypePreorder,
            is_type_shared: false,
            is_type_delivery: isTypeDelivery,
            price: price,
            store_default_bread_ref: storeDefaultBreadRef,
            updated_at: now,
          }
          // 受付設定のオプションをまとめる
          const options: OptionType[] = []
          if (expirationDate !== '') {
            options.push({ key: '賞味期限', value: expirationDate })
          }
          const nextStoreDefaultSchedules: StoreDefaultScheduleType[] = [
            ...dailySchedules.map((schedule) => ({ ...schedule, options })),
            ...preorderSchedules.map((schedule) => ({ ...schedule, options })),
          ]
          const nextStoreDefaultDeliveryBread: StoreDefaultDeliveryBreadType = {
            ...storeDefaultDeliveryBread,
            store_default_bread_ref: storeDefaultBreadRef,
            maximum_order: storeDeliveryBreadForm.maximumOrder,
            package_size: storeDeliveryBreadForm.packageSize as PackageSize,
            delivery_company: storeDeliveryBreadForm.deliveryCompany,
            frozen_delivery_type: storeDeliveryBreadForm.frozenDeliveryType,
            is_frozen_delivery: storeDeliveryBreadForm.isFrozenDelivery,
            hours_to_deadline: storeDeliveryBreadForm.hoursToDeadline,
            how_to_eat: storeDeliveryBreadForm.howToEat,
            how_to_store: storeDeliveryBreadForm.howToStore,
            choices: storeDeliveryBreadForm.choices,
            options,
          }
          dispatch(
            saveDefault({
              nextStoreDefault,
              nextStoreDefaultBread,
              nextStoreDefaultSchedules,
              nextStoreDefaultDeliveryBread,
              prevStoreDefaultSchedules: storeDefaultSchedules,
              prevStoreDefaultDeliveryBread: storeDefaultDeliveryBread,
            })
          )
        }}
        size={'medium'}
        type={'main'}
      />
      <DefaultEditError
        description={parseDescriptionToHtml()}
        categoryId={categoryId}
        isScheduledGeneration={isScheduledGeneration}
        isTypeDaily={isTypeDaily}
        dailySchedules={dailySchedules}
        isTypePreorder={isTypePreorder}
        preorderSchedules={preorderSchedules}
        isTypeDelivery={isTypeDelivery}
        storeDeliveryBreadForm={storeDeliveryBreadForm}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ProductDefaultEditTemplate
